import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Descriptions,
  Input,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatMoney } from '@/utils/format';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { epcEstimatingLibraryService } from '@/services/epcEstimatingLibrary.service';
import { getEst3CostProvenance } from '../est3CostProvenance';
import {
  COSTING_METHODS,
  COSTING_METHOD_LABELS,
  computeUnitDirectCost,
  needsMethodConfirm,
  type CostingMethodCode,
  type MethodTransitionMode,
} from './costingMethodHelpers';
import styles from './EpcEstimatingWorkbench.module.css';

const { Text } = Typography;

export interface EpcEstimatingCostingPanelProps {
  estimateId: string;
  revisionId: string;
  selectedBoqLineId: string;
  costLines?: any[];
  readOnly?: boolean;
  /** Reload BOQ / cost / detail after method, assembly, or rate apply. */
  onMutated?: () => void;
}

export const EpcEstimatingCostingPanel: React.FC<EpcEstimatingCostingPanelProps> = ({
  estimateId,
  revisionId,
  selectedBoqLineId,
  costLines = [],
  readOnly,
  onMutated,
}) => {
  const qc = useQueryClient();
  const [methodDraft, setMethodDraft] = useState<CostingMethodCode | null>(null);
  const [methodReason, setMethodReason] = useState('');
  const [transitionMode, setTransitionMode] = useState<MethodTransitionMode>('REPLACE');
  const [methodPreview, setMethodPreview] = useState<any>(null);
  const [assemblySearch, setAssemblySearch] = useState('');
  const [assemblyId, setAssemblyId] = useState<string | null>(null);
  const [assemblyVersionId, setAssemblyVersionId] = useState<string | null>(null);
  const [assemblyPreview, setAssemblyPreview] = useState<any>(null);
  const [selectedCostLineId, setSelectedCostLineId] = useState<string | null>(null);
  const [crewTemplateId, setCrewTemplateId] = useState<string | null>(null);
  const [productivityNormId, setProductivityNormId] = useState<string | null>(null);
  const [wasteDraft, setWasteDraft] = useState('');
  const [wasteReason, setWasteReason] = useState('');
  const [assemblyApplyReason, setAssemblyApplyReason] = useState('');

  const detailQ = useQuery({
    queryKey: ['epc-est-boq-detail', estimateId, revisionId, selectedBoqLineId],
    queryFn: () =>
      epcEstimationService.getClientBoqLineDetail(estimateId, revisionId, selectedBoqLineId),
    enabled: !!estimateId && !!revisionId && !!selectedBoqLineId,
  });

  const methodQ = useQuery({
    queryKey: ['epc-est-costing-method', estimateId, revisionId, selectedBoqLineId],
    queryFn: () =>
      epcEstimationService.getCostingMethod(estimateId, revisionId, {
        boqLineId: selectedBoqLineId,
      }),
    enabled: !!estimateId && !!revisionId && !!selectedBoqLineId,
  });

  const assembliesQ = useQuery({
    queryKey: ['epc-est-lib-assemblies'],
    queryFn: () => epcEstimatingLibraryService.listAssemblies(),
    staleTime: 60_000,
  });

  const assemblyDetailQ = useQuery({
    queryKey: ['epc-est-lib-assembly', assemblyId],
    queryFn: () => epcEstimatingLibraryService.getAssembly(assemblyId!),
    enabled: !!assemblyId,
  });

  const crewsQ = useQuery({
    queryKey: ['epc-est-crew-templates', estimateId],
    queryFn: () => epcEstimationService.listCrewTemplates(estimateId),
    staleTime: 60_000,
  });

  const normsQ = useQuery({
    queryKey: ['epc-est-productivity-norms', estimateId],
    queryFn: () => epcEstimationService.listProductivityNorms(estimateId),
    staleTime: 60_000,
  });

  useEffect(() => {
    setMethodDraft(null);
    setMethodPreview(null);
    setMethodReason('');
    setTransitionMode('REPLACE');
    setAssemblyId(null);
    setAssemblyVersionId(null);
    setAssemblyPreview(null);
    setSelectedCostLineId(null);
    setCrewTemplateId(null);
    setProductivityNormId(null);
    setWasteDraft('');
    setWasteReason('');
    setAssemblyApplyReason('');
  }, [selectedBoqLineId]);

  useEffect(() => {
    if (!assemblyDetailQ.data) return;
    const versions = (assemblyDetailQ.data.versions || []) as any[];
    const published = versions.find((v) => String(v.status).toUpperCase() === 'PUBLISHED');
    setAssemblyVersionId(published ? String(published.id) : null);
    setAssemblyPreview(null);
  }, [assemblyDetailQ.data]);

  const linked = useMemo(() => {
    if (detailQ.data?.linked_cost_lines) return detailQ.data.linked_cost_lines as any[];
    return (costLines || []).filter((c) => c.client_boq_line_id === selectedBoqLineId);
  }, [detailQ.data, costLines, selectedBoqLineId]);

  useEffect(() => {
    if (!linked.length) {
      setSelectedCostLineId(null);
      return;
    }
    if (!selectedCostLineId || !linked.some((l) => l.id === selectedCostLineId)) {
      setSelectedCostLineId(String(linked[0].id));
    }
  }, [linked, selectedCostLineId]);

  const selectedCostLine = useMemo(
    () => linked.find((l) => l.id === selectedCostLineId) || null,
    [linked, selectedCostLineId]
  );

  /** Only pass master refs to Rate Resolver — assembly component UUIDs are not rate keys. */
  const rateResourceRefId = useMemo(() => {
    if (!selectedCostLine) return undefined;
    const refType = String(selectedCostLine.source_ref_type || '').toUpperCase();
    if (
      !refType ||
      refType === 'ASSEMBLY_COMPONENT' ||
      refType === 'ASSEMBLY' ||
      refType === 'MANUAL'
    ) {
      return undefined;
    }
    return selectedCostLine.source_ref_id ? String(selectedCostLine.source_ref_id) : undefined;
  }, [selectedCostLine]);

  const ratesQ = useQuery({
    queryKey: [
      'epc-est-rate-candidates',
      estimateId,
      selectedCostLine?.id,
      selectedCostLine?.resource_kind,
      rateResourceRefId,
    ],
    queryFn: () =>
      epcEstimationService.fetchRateCandidates(estimateId, {
        revisionId,
        resourceKind: String(selectedCostLine!.resource_kind || 'MATERIAL'),
        resourceRefId: rateResourceRefId,
        resourceRefType: selectedCostLine!.source_ref_type || undefined,
        uom: selectedCostLine!.uom || undefined,
        currency: selectedCostLine!.currency || undefined,
        quantity:
          selectedCostLine!.quantity != null ? Number(selectedCostLine!.quantity) : undefined,
      }),
    enabled: !!selectedCostLine?.id && !!selectedCostLine?.resource_kind,
  });

  const effectiveMethod = (methodQ.data?.effectiveMethod ||
    'RESOURCE_BUILDUP') as CostingMethodCode;
  const sourceLevel = methodQ.data?.sourceLevel || 'SYSTEM';
  const isOverride = sourceLevel === 'BOQ';
  const activeMethod = (methodDraft || effectiveMethod) as CostingMethodCode;
  const assemblyEnabled = activeMethod === 'ASSEMBLY' || !!assemblyId || !!assemblyPreview;
  const showCrew =
    activeMethod === 'CREW_BASED' ||
    String(selectedCostLine?.resource_kind || '').toUpperCase() === 'LABOUR';
  const wbsReco = methodQ.data?.packageRecommendation || null;
  const hasWbsReco =
    !!wbsReco &&
    !!(
      wbsReco.method ||
      wbsReco.recommended_assembly_id ||
      wbsReco.recommended_crew_template_id ||
      wbsReco.recommended_productivity_norm_id
    );

  const publishedAssemblies = useMemo(() => {
    const rows = (assembliesQ.data || []) as any[];
    const q = assemblySearch.trim().toLowerCase();
    return rows
      .filter((a) => a.published_version_no != null)
      .filter((a) =>
        !q
          ? true
          : `${a.code || ''} ${a.name || ''}`.toLowerCase().includes(q)
      );
  }, [assembliesQ.data, assemblySearch]);

  const bump = () => {
    void qc.invalidateQueries({ queryKey: ['epc-est-boq-detail', estimateId, revisionId] });
    void qc.invalidateQueries({
      queryKey: ['epc-est-costing-method', estimateId, revisionId, selectedBoqLineId],
    });
    void qc.invalidateQueries({ queryKey: ['epc-est-rate-candidates', estimateId] });
    onMutated?.();
  };

  const previewMethodM = useMutation({
    mutationFn: (method: CostingMethodCode) =>
      epcEstimationService.previewBoqCostingMethod(estimateId, selectedBoqLineId, {
        revisionId,
        method,
      }),
    onSuccess: (data, method) => {
      setMethodDraft(method);
      setMethodPreview(data);
      if (!needsMethodConfirm(data)) {
        message.info('No cost lines affected — confirm with a reason to set override.');
      }
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Preview failed'),
  });

  const confirmMethodM = useMutation({
    mutationFn: () => {
      if (!methodDraft) throw new Error('Select a method');
      return epcEstimationService.confirmBoqCostingMethod(estimateId, selectedBoqLineId, {
        revisionId,
        method: methodDraft,
        reason: methodReason.trim(),
        transitionMode,
      });
    },
    onSuccess: () => {
      message.success('Costing method updated');
      setMethodPreview(null);
      setMethodDraft(null);
      setMethodReason('');
      bump();
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Confirm failed'),
  });

  const previewAssemblyM = useMutation({
    mutationFn: () => {
      if (!assemblyVersionId) throw new Error('No published version');
      return epcEstimationService.previewBoqAssembly(estimateId, selectedBoqLineId, {
        revisionId,
        assemblyVersionId,
      });
    },
    onSuccess: (data) => setAssemblyPreview(data),
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Assembly preview failed'),
  });

  const applyAssemblyM = useMutation({
    mutationFn: (opts?: { reason?: string }) => {
      if (!assemblyVersionId) throw new Error('No published version');
      const replacing = linked.length > 0;
      const reason =
        String(opts?.reason || assemblyApplyReason || '').trim() ||
        (replacing ? '' : `Applied assembly from Workbench`);
      if (replacing && !reason) {
        throw new Error('Reason required when replacing existing build-up');
      }
      return epcEstimationService.applyBoqAssembly(estimateId, selectedBoqLineId, {
        revisionId,
        assemblyVersionId,
        reason,
        replaceExisting: replacing,
      });
    },
    onSuccess: () => {
      message.success('Assembly applied');
      setAssemblyPreview(null);
      setAssemblyApplyReason('');
      bump();
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Assembly apply failed'),
  });

  const applyCrewM = useMutation({
    mutationFn: () => {
      if (!selectedCostLineId) throw new Error('Select a LABOUR cost line');
      if (!crewTemplateId) throw new Error('Select a crew template');
      return epcEstimationService.applyCrew(estimateId, selectedCostLineId, {
        revisionId,
        crewTemplateId,
        productivityNormId: productivityNormId || undefined,
      });
    },
    onSuccess: () => {
      message.success('Crew applied');
      bump();
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Crew apply failed'),
  });

  const patchWasteM = useMutation({
    mutationFn: () => {
      if (!selectedCostLineId) throw new Error('Select a cost line');
      const waste = Number(wasteDraft);
      if (!Number.isFinite(waste) || waste < 0) throw new Error('Invalid wastage');
      if (!wasteReason.trim()) throw new Error('Reason required for wastage override');
      return epcEstimationService.patchCostLine(estimateId, selectedCostLineId, revisionId, {
        wasteFactor: waste,
        waste_factor: waste,
        overrideReason: wasteReason.trim(),
        notes: `Wastage override: ${wasteReason.trim()}`,
      });
    },
    onSuccess: () => {
      message.success('Wastage updated — backend recalculated');
      setWasteReason('');
      bump();
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Wastage update failed'),
  });

  const applyRateM = useMutation({
    mutationFn: (candidate: Record<string, unknown>) => {
      if (!selectedCostLineId) throw new Error('No cost line');
      const isManual = String(candidate.source_type || '').toUpperCase() === 'MANUAL';
      return epcEstimationService.applyRateCandidate(estimateId, selectedCostLineId, {
        revisionId,
        candidate,
        reason: isManual ? 'Applied MANUAL rate from Workbench COSTING' : undefined,
      });
    },
    onSuccess: () => {
      message.success('Rate applied');
      bump();
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Apply rate failed'),
  });

  const unitDirect = selectedCostLine ? computeUnitDirectCost(selectedCostLine) : null;
  const provenance = selectedCostLine ? getEst3CostProvenance(selectedCostLine) : null;
  const rateCandidates = (ratesQ.data?.candidates || ratesQ.data?.items || []) as any[];
  const rateNotes = (ratesQ.data?.notes || []) as string[];

  useEffect(() => {
    if (selectedCostLine?.waste_factor != null) {
      setWasteDraft(String(selectedCostLine.waste_factor));
    }
  }, [selectedCostLine?.id, selectedCostLine?.waste_factor]);

  return (
    <div className={styles.costingPanel} data-testid="estimating-costing-panel">
      {hasWbsReco ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
          message="WBS package recommendation"
          description={
            <Text style={{ fontSize: 12 }}>
              {[
                wbsReco.method ? `Method: ${wbsReco.method}` : null,
                wbsReco.recommended_assembly_id ? 'Assembly suggested' : null,
                wbsReco.recommended_crew_template_id ? 'Crew suggested' : null,
                wbsReco.recommended_productivity_norm_id ? 'Norm suggested' : null,
              ]
                .filter(Boolean)
                .join(' · ') || 'Package knowledge available — apply only with confirmation.'}
            </Text>
          }
        />
      ) : null}

      <section className={styles.costingSection}>
        <div className={styles.costingSectionTitle}>Method</div>
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Effective">
            <Space size={6} wrap>
              <Tag color="blue">{effectiveMethod}</Tag>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {COSTING_METHOD_LABELS[effectiveMethod] || effectiveMethod}
              </Text>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Source">
            <Tag>{sourceLevel}</Tag>
            {isOverride ? <Tag color="orange">Override</Tag> : null}
          </Descriptions.Item>
          {methodQ.data?.overrideReason ? (
            <Descriptions.Item label="Reason">{String(methodQ.data.overrideReason)}</Descriptions.Item>
          ) : null}
        </Descriptions>

        {!readOnly ? (
          <Space direction="vertical" style={{ width: '100%', marginTop: 8 }} size={6}>
            <Select
              size="small"
              style={{ width: '100%' }}
              placeholder="Change method…"
              value={methodDraft ?? undefined}
              options={COSTING_METHODS.map((m) => ({
                value: m,
                label: `${m} — ${COSTING_METHOD_LABELS[m]}`,
              }))}
              onChange={(v) => {
                setMethodDraft(v);
                setMethodPreview(null);
              }}
              allowClear
              data-testid="costing-method-select"
            />
            <Button
              size="small"
              block
              disabled={!methodDraft || methodDraft === effectiveMethod}
              loading={previewMethodM.isPending}
              onClick={() => methodDraft && previewMethodM.mutate(methodDraft)}
              data-testid="costing-method-preview"
            >
              Preview change
            </Button>
            {methodPreview ? (
              <>
                {methodPreview.warning ? (
                  <Alert type="warning" showIcon message={methodPreview.warning} />
                ) : (
                  <Alert
                    type="info"
                    showIcon
                    message={`${methodPreview.from} → ${methodPreview.to} · ${methodPreview.affectedCostLineCount || 0} cost line(s)`}
                  />
                )}
                <Radio.Group
                  size="small"
                  value={transitionMode}
                  onChange={(e) => setTransitionMode(e.target.value)}
                >
                  <Radio.Button value="REPLACE">Replace</Radio.Button>
                  <Radio.Button value="PRESERVE">Preserve</Radio.Button>
                </Radio.Group>
                <Input.TextArea
                  size="small"
                  rows={2}
                  placeholder="Reason (required)"
                  value={methodReason}
                  onChange={(e) => setMethodReason(e.target.value)}
                  data-testid="costing-method-reason"
                />
                <Space size={6} wrap style={{ width: '100%' }}>
                  <Button
                    type="primary"
                    size="small"
                    style={{ flex: 1 }}
                    disabled={!methodReason.trim()}
                    loading={confirmMethodM.isPending}
                    onClick={() => confirmMethodM.mutate()}
                    data-testid="costing-method-confirm"
                  >
                    Confirm method
                  </Button>
                  <Button
                    size="small"
                    data-testid="costing-method-cancel"
                    onClick={() => {
                      setMethodPreview(null);
                      setMethodDraft(null);
                      setMethodReason('');
                    }}
                  >
                    Cancel
                  </Button>
                </Space>
              </>
            ) : null}
          </Space>
        ) : null}
      </section>

      {assemblyEnabled ? (
        <section className={styles.costingSection} data-testid="costing-assembly-section">
          <div className={styles.costingSectionTitle}>Assembly</div>
          {!readOnly ? (
            <Space direction="vertical" style={{ width: '100%' }} size={6}>
              <Input.Search
                size="small"
                allowClear
                placeholder="Search published assemblies"
                onSearch={setAssemblySearch}
                onChange={(e) => {
                  if (!e.target.value) setAssemblySearch('');
                }}
              />
              <Select
                size="small"
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="label"
                placeholder="Select assembly"
                loading={assembliesQ.isLoading}
                value={assemblyId ?? undefined}
                options={publishedAssemblies.map((a) => ({
                  value: a.id,
                  label: `${a.code} · ${a.name} (v${a.published_version_no})`,
                }))}
                onChange={(id) => {
                  setAssemblyId(id);
                  setAssemblyPreview(null);
                }}
                allowClear
              />
              {!assemblyVersionId && assemblyId ? (
                <Alert type="warning" showIcon message="No PUBLISHED version on this assembly" />
              ) : null}
              {linked.length > 0 ? (
                <Input.TextArea
                  size="small"
                  rows={2}
                  placeholder="Reason required when replacing existing build-up"
                  value={assemblyApplyReason}
                  onChange={(e) => setAssemblyApplyReason(e.target.value)}
                />
              ) : null}
              <Space size={6} wrap>
                <Button
                  size="small"
                  disabled={!assemblyVersionId}
                  loading={previewAssemblyM.isPending}
                  onClick={() => previewAssemblyM.mutate()}
                >
                  Preview
                </Button>
                <Button
                  type="primary"
                  size="small"
                  disabled={
                    !assemblyVersionId || (linked.length > 0 && !assemblyApplyReason.trim())
                  }
                  loading={applyAssemblyM.isPending}
                  onClick={() => applyAssemblyM.mutate({ reason: assemblyApplyReason })}
                >
                  Apply
                </Button>
              </Space>
              {assemblyPreview ? (
                <>
                  {assemblyPreview.warning ? (
                    <Alert type="warning" showIcon message={assemblyPreview.warning} />
                  ) : null}
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {assemblyPreview.assemblyCode} · v{assemblyPreview.versionNo} ·{' '}
                    {assemblyPreview.componentCount} component(s) · output{' '}
                    {assemblyPreview.outputQty} {assemblyPreview.outputUom}
                  </Text>
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="id"
                    dataSource={assemblyPreview.components || []}
                    scroll={{ x: 280 }}
                    columns={[
                      { title: 'Kind', dataIndex: 'resourceKind', width: 72 },
                      { title: 'Desc', dataIndex: 'description', ellipsis: true },
                      {
                        title: 'Qty',
                        dataIndex: 'projectedQuantity',
                        width: 56,
                        render: (v) => (v != null ? Number(v).toFixed(2) : '—'),
                      },
                      {
                        title: 'Rate',
                        dataIndex: 'defaultRate',
                        width: 56,
                        render: (v) =>
                          formatMoney(v, { display: 'plain', maximumFractionDigits: 2 }),
                      },
                    ]}
                  />
                </>
              ) : null}
            </Space>
          ) : (
            <Text type="secondary">Read-only — assembly apply disabled</Text>
          )}
        </section>
      ) : (
        <section className={styles.costingSection} data-testid="costing-assembly-section">
          <div className={styles.costingSectionTitle}>Assembly</div>
          <Alert
            type="info"
            showIcon
            message="Set method to Assembly to select and apply a published assembly"
            action={
              readOnly ? undefined : (
                <Button
                  size="small"
                  type="link"
                  onClick={() => {
                    setMethodDraft('ASSEMBLY');
                    setTransitionMode(linked.length ? 'REPLACE' : 'PRESERVE');
                  }}
                >
                  Use Assembly
                </Button>
              )
            }
          />
        </section>
      )}

      <section className={styles.costingSection}>
        <div className={styles.costingSectionTitle}>Build-up</div>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {linked.length} cost line(s)
        </Text>
        <Table
          size="small"
          pagination={false}
          rowKey="id"
          dataSource={linked}
          style={{ marginTop: 4 }}
          rowClassName={(row) =>
            row.id === selectedCostLineId ? styles.costingRowSelected : ''
          }
          onRow={(row) => ({
            onClick: () => setSelectedCostLineId(String(row.id)),
            style: { cursor: 'pointer' },
          })}
          columns={[
            { title: 'Kind', dataIndex: 'resource_kind', width: 72 },
            { title: 'Description', dataIndex: 'description', ellipsis: true },
            {
              title: 'Total',
              dataIndex: 'total_amount',
              width: 64,
              render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 0 }),
            },
          ]}
        />
      </section>

      <section className={styles.costingSection} data-testid="costing-rates-section">
        <div className={styles.costingSectionTitle}>Rate Candidates</div>
        {!selectedCostLine ? (
          <Alert type="info" showIcon message="Select a cost line in Build-up" />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={6}>
            <Text style={{ fontSize: 12 }}>
              {selectedCostLine.resource_kind} · {selectedCostLine.description || 'Cost line'}
            </Text>
            {rateNotes.map((n, i) => (
              <Alert key={i} type="info" showIcon message={n} />
            ))}
            <Table
              size="small"
              loading={ratesQ.isLoading}
              pagination={false}
              rowKey={(r, i) => `${r.source_id || r.source_type}-${i}`}
              dataSource={rateCandidates}
              scroll={{ x: 360 }}
              locale={{ emptyText: 'No candidates' }}
              columns={[
                {
                  title: '#',
                  dataIndex: 'rank',
                  width: 36,
                },
                {
                  title: 'Rate',
                  dataIndex: 'rate',
                  width: 64,
                  render: (v) =>
                    formatMoney(v, { display: 'plain', maximumFractionDigits: 4 }),
                },
                {
                  title: 'Norm',
                  dataIndex: 'normalized_rate',
                  width: 64,
                  render: (v, row) =>
                    v != null
                      ? formatMoney(v, { display: 'plain', maximumFractionDigits: 4 })
                      : row.uom_status === 'INCOMPATIBLE'
                        ? '!'
                        : '—',
                },
                { title: 'Cur', dataIndex: 'currency', width: 44 },
                { title: 'UOM', dataIndex: 'uom', width: 44, render: (v) => v || '—' },
                {
                  title: '→UOM',
                  dataIndex: 'normalized_uom',
                  width: 48,
                  render: (v, row) =>
                    row.uom_status === 'CONVERTED' ? (
                      <Tag color="blue">{v}</Tag>
                    ) : (
                      v || '—'
                    ),
                },
                {
                  title: 'Source',
                  dataIndex: 'source_type',
                  width: 88,
                  ellipsis: true,
                },
                {
                  title: 'Date',
                  dataIndex: 'effective_at',
                  width: 84,
                  render: (v) => (v ? String(v).slice(0, 10) : '—'),
                },
                {
                  title: 'Why',
                  dataIndex: 'rank_reason',
                  ellipsis: true,
                  width: 100,
                },
                {
                  title: '',
                  width: 56,
                  fixed: 'right' as const,
                  render: (_: unknown, row: any) =>
                    readOnly ? null : (
                      <Button
                        type="link"
                        size="small"
                        loading={applyRateM.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          applyRateM.mutate(row);
                        }}
                      >
                        Apply
                      </Button>
                    ),
                },
              ]}
            />
          </Space>
        )}
      </section>

      {showCrew ? (
        <section className={styles.costingSection}>
          <div className={styles.costingSectionTitle}>Crew · Productivity</div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Crew cost / period ÷ production / period = cost / output unit (backend).
          </Text>
          {!readOnly ? (
            <Space direction="vertical" style={{ width: '100%', marginTop: 6 }} size={6}>
              <Select
                size="small"
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="label"
                placeholder="Crew template"
                loading={crewsQ.isLoading}
                value={crewTemplateId ?? undefined}
                options={(crewsQ.data || []).map((c: any) => ({
                  value: c.id,
                  label: `${c.code || ''} · ${c.name || c.id}`,
                }))}
                onChange={setCrewTemplateId}
                allowClear
              />
              <Select
                size="small"
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="label"
                placeholder="Productivity norm (optional)"
                loading={normsQ.isLoading}
                value={productivityNormId ?? undefined}
                options={(normsQ.data || []).map((n: any) => ({
                  value: n.id,
                  label: `${n.code || ''} · ${n.name || n.id}${
                    n.output_qty != null
                      ? ` (${n.output_qty} ${n.output_uom || ''}/${n.period_basis || 'period'})`
                      : ''
                  }`,
                }))}
                onChange={setProductivityNormId}
                allowClear
              />
              {String(selectedCostLine?.resource_kind || '').toUpperCase() !== 'LABOUR' ? (
                <Alert
                  type="warning"
                  showIcon
                  message="Select a LABOUR cost line in Build-up to apply crew snapshot"
                />
              ) : null}
              <Button
                type="primary"
                size="small"
                block
                disabled={
                  !crewTemplateId ||
                  String(selectedCostLine?.resource_kind || '').toUpperCase() !== 'LABOUR'
                }
                loading={applyCrewM.isPending}
                onClick={() => applyCrewM.mutate()}
              >
                Apply crew snapshot
              </Button>
            </Space>
          ) : (
            <Text type="secondary">Read-only</Text>
          )}
        </section>
      ) : null}

      <section className={styles.costingSection}>
        <div className={styles.costingSectionTitle}>Wastage</div>
        {!selectedCostLine ? (
          <Text type="secondary">Select a cost line</Text>
        ) : readOnly ? (
          <Text>{selectedCostLine.waste_factor ?? '—'}</Text>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={6}>
            <Input
              size="small"
              addonBefore="Factor"
              value={wasteDraft}
              onChange={(e) => setWasteDraft(e.target.value)}
            />
            <Input.TextArea
              size="small"
              rows={2}
              placeholder="Override reason (required)"
              value={wasteReason}
              onChange={(e) => setWasteReason(e.target.value)}
            />
            <Button
              size="small"
              block
              disabled={!wasteReason.trim()}
              loading={patchWasteM.isPending}
              onClick={() => patchWasteM.mutate()}
            >
              Override wastage
            </Button>
          </Space>
        )}
      </section>

      <section className={styles.costingSection}>
        <div className={styles.costingSectionTitle}>Provenance</div>
        {!selectedCostLine ? (
          <Text type="secondary">—</Text>
        ) : (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="rate_source">
              {selectedCostLine.rate_source || provenance?.rateSource || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="fx_rate">
              {selectedCostLine.fx_rate ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label="waste_factor">
              {selectedCostLine.waste_factor ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label="productivity">
              {selectedCostLine.productivity_factor ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Assembly">
              {selectedCostLine.source_assembly_id
                ? `v:${String(selectedCostLine.source_assembly_version_id || '').slice(0, 8)}…`
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Ref">
              {provenance?.sourceRefLabel || provenance?.sourceRefType || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Unit direct">
              {unitDirect != null
                ? formatMoney(unitDirect, { display: 'plain', maximumFractionDigits: 4 })
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Line total">
              {formatMoney(selectedCostLine.total_amount, {
                display: 'plain',
                maximumFractionDigits: 0,
              })}
            </Descriptions.Item>
          </Descriptions>
        )}
      </section>
    </div>
  );
};
